import React from 'react'
import {
    Box,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Switch,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import { INVITE_EMAIL_TEMPLATE_OPTIONS } from '../inviteEmailTemplateOptions'
import { StepEditPanelProps } from '../pipelineBuilder.types'
import {
    ApplicationHandbookScope,
    LeadsStepConfig,
    StepEnterBehavior,
} from '../stepConfig.types'
import { getStepConfig } from '../stepConfigUtils'
import { resolveStepEnterBehavior } from '../CommonStepFields'

const useStyles = makeStyles((theme: Theme) => ({
    section: {
        marginBottom: theme.spacing(2.5),
    },
    sectionTitle: {
        fontWeight: 600,
        fontSize: '0.8125rem',
        marginBottom: theme.spacing(1),
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    subsectionTitle: {
        fontWeight: 600,
        fontSize: '0.875rem',
        marginBottom: theme.spacing(1),
        color: theme.palette.text.primary,
    },
    subsection: {
        marginBottom: theme.spacing(2),
    },
    radioGroup: {
        marginLeft: theme.spacing(0.5),
    },
    fieldBlock: {
        marginTop: theme.spacing(1),
    },
}))

function resolveLeadsEnterBehavior(
    step: StepEditPanelProps['step'],
): 'auto_start' | 'queue' {
    const behavior = resolveStepEnterBehavior(step)
    return behavior === 'pm_start'
        ? 'queue'
        : behavior === 'queue'
        ? 'queue'
        : 'auto_start'
}

export const LeadsStepPanel: React.FC<StepEditPanelProps> = ({
    step,
    readOnly = false,
    onChange,
}) => {
    const classes = useStyles()
    const config = getStepConfig(step, 'leads')
    const invite = config.on_enter_automations.send_account_invite ?? {
        enabled: true,
    }
    const handbookScope =
        config.application_handbook?.scope ?? 'workbook_complete'
    const enterBehavior = resolveLeadsEnterBehavior(step)

    const updateConfig = (patch: Partial<LeadsStepConfig>) => {
        onChange({
            config: { ...config, ...patch },
        })
    }

    const updateInvite = (
        patch: Partial<
            NonNullable<
                LeadsStepConfig['on_enter_automations']['send_account_invite']
            >
        >,
    ) => {
        updateConfig({
            on_enter_automations: {
                ...config.on_enter_automations,
                send_account_invite: { ...invite, ...patch },
            },
        })
    }

    const updateEnterBehavior = (value: 'auto_start' | 'queue') => {
        onChange({ enter_behavior: value as StepEnterBehavior })
    }

    const updateHandbookScope = (scope: ApplicationHandbookScope) => {
        updateConfig({
            application_handbook: { scope },
        })
    }

    const selectedTemplateId =
        invite.template_id ?? INVITE_EMAIL_TEMPLATE_OPTIONS[0]?.id ?? ''

    return (
        <Box>
            <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>
                    When someone becomes a lead
                </Typography>
                <RadioGroup
                    className={classes.radioGroup}
                    value={enterBehavior}
                    onChange={(e) =>
                        updateEnterBehavior(
                            e.target.value as 'auto_start' | 'queue',
                        )
                    }
                >
                    <FormControlLabel
                        value="auto_start"
                        control={
                            <Radio
                                color="primary"
                                size="small"
                                disabled={readOnly}
                            />
                        }
                        label="Auto-start — Begin First Item"
                    />
                    <FormControlLabel
                        value="queue"
                        control={
                            <Radio
                                color="primary"
                                size="small"
                                disabled={readOnly}
                            />
                        }
                        label="Place in list — Await PM action"
                    />
                </RadioGroup>
            </Box>

            <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>
                    Lead processing
                </Typography>

                <Box className={classes.subsection}>
                    <Typography className={classes.subsectionTitle}>
                        Account creation
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={invite.enabled ?? true}
                                onChange={(_, checked) =>
                                    updateInvite({ enabled: checked })
                                }
                                disabled={readOnly}
                                color="primary"
                            />
                        }
                        label="Send invite email"
                    />
                    <Box className={classes.fieldBlock}>
                        <FormControl
                            variant="outlined"
                            size="small"
                            fullWidth
                            disabled={readOnly || invite.enabled === false}
                        >
                            <InputLabel id="invite-template-select-label">
                                Invite email template
                            </InputLabel>
                            <Select
                                labelId="invite-template-select-label"
                                label="Invite email template"
                                value={selectedTemplateId}
                                onChange={(e) =>
                                    updateInvite({
                                        template_id: String(e.target.value),
                                    })
                                }
                            >
                                {INVITE_EMAIL_TEMPLATE_OPTIONS.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                <Box className={classes.subsection}>
                    <Typography className={classes.subsectionTitle}>
                        Application handbook
                    </Typography>
                    <RadioGroup
                        className={classes.radioGroup}
                        value={handbookScope}
                        onChange={(e) =>
                            updateHandbookScope(
                                e.target.value as ApplicationHandbookScope,
                            )
                        }
                    >
                        <FormControlLabel
                            value="workbook_complete"
                            control={
                                <Radio
                                    color="primary"
                                    size="small"
                                    disabled={readOnly}
                                />
                            }
                            label="Request entire Application Handbook completion"
                        />
                        <FormControlLabel
                            value="identity_complete"
                            control={
                                <Radio
                                    color="primary"
                                    size="small"
                                    disabled={readOnly}
                                />
                            }
                            label="Request only Identity Application Handbook completion"
                        />
                    </RadioGroup>
                </Box>
            </Box>
        </Box>
    )
}
